import React, { useState, useEffect } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { formatPhoneNumber } from '../utils/Common';
import { createReturnShipment, notifyRequestCancellation } from '../utils/bedrockAPI';
import LoaderButton from './LoaderButton';    
import packaged4cam from '../images/packaged-4cam-system.jpg';
import packaged2cam from '../images/packaged-2cam-system.jpg';
import packagedlegacy from '../images/packaged_camera_system.jpg';
import styled from 'styled-components';
import AddressDisplay from './AddressDisplay';

const ReturnImage = styled.img`
  display: block;
  margin: 20px auto;
  max-width: 100%;
  height: auto;
`;

export default function CancelServiceModal({ show, onHide, cap, stripeCustomer, builderId }) {
  const [step, setStep] = useState(1);
  const [hasOriginalBox, setHasOriginalBox] = useState(null);
  const [hasReturnLabel, setHasReturnLabel] = useState(null);
  const [shippingAddress, setShippingAddress] = useState(null);
  const [isEditingAddress, setIsEditingAddress] = useState(false);
  const [returnImage, setReturnImage] = useState(packagedlegacy);
  const [isGeneratingLabel, setIsGeneratingLabel] = useState(false);

  useEffect(() => {
    if (stripeCustomer?.shipping) {
      const shippingName = stripeCustomer.shipping.name;
      const companyName = stripeCustomer.metadata?.company_name || shippingName;
      const phone = formatPhoneNumber(stripeCustomer.phone);
      
      setShippingAddress({
        company: companyName !== shippingName ? companyName : null,
        name: shippingName,
        phone,
        street: stripeCustomer.shipping.address.line1,
        city: stripeCustomer.shipping.address.city,
        state: stripeCustomer.shipping.address.state,
        zip: stripeCustomer.shipping.address.postal_code
      });
    }
  }, [stripeCustomer]);

  useEffect(() => {
    if(cap && cap.architecture.includes("4CAM")) {
        setReturnImage(packaged4cam);
    } else if(cap && cap.architecture.includes("2CAM")) {
        setReturnImage(packaged2cam);
    } else {
        setReturnImage(packagedlegacy);
    }
  }, [cap]);

  const handleClose = () => {
    setStep(1);
    setHasOriginalBox(null);
    setHasReturnLabel(null);
    onHide();
  };

  const handleGenerateLabel = async () => {
    setIsGeneratingLabel(true);
    try {
      const response = await createReturnShipment(cap.capId);
      if (response.success && response.returnLabelUrl) {
        // Fetch the PDF as a blob and download it
        const pdfResponse = await fetch(response.returnLabelUrl);
        const pdfBlob = await pdfResponse.blob();
        const url = window.URL.createObjectURL(pdfBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `return-label-${cap.capId}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url); // Clean up the blob URL
        await notifyRequestCancellation(cap.capId, false, true);
        return response.returnLabelUrl;
      } else {
        console.error('Failed to generate return label:', response.message);
        setIsGeneratingLabel(false);
        return null;
      }
    } catch (error) {
      console.error('Error generating return label:', error);
      setIsGeneratingLabel(false);
      return null;
    } finally {
      setIsGeneratingLabel(false);
    }
  };

  const requestCancellation = async (packaging) => {
    try {
      const response = await notifyRequestCancellation(cap.capId, packaging, false);;
      if (response.success) {
        setStep(4);
      }
    } catch (error) {
      console.error('Error requesting cancellation:', error);
    }
  };
  
  const ReturnInstructions = ({packaging}) => {
    return (
        <div>
          <h4 className="modal-step-title">{!packaging ? "Once you receive your new packaging, please note the following steps:" : "Please note the following steps:"}</h4>
          <ol className="modal-step-list">
            <li>Place the camera system securely into the foam packaging</li>
            <li>Use the provided foam to secure the system</li>
            <li>Do not worry if you’ve misplaced the camera mounting hardware and accessories. All we need is the main camera system.</li>
            <li>Place the return label over the old sticker on the outside of the box</li>
            <li>Tape up the box, sometimes FedEx will do this for you for a small fee</li>
            <li>Simply drop it off at your nearest FedEx location</li>
          </ol>
          <p className="modal-step-description">Your subscription automatically cancels as soon as the return label is scanned by FedEx.<br />
          If you have cancelled in error, please contact: <a href="mailto:help@bedrockwireless.com">help@bedrockwireless.com</a>
          <br /><br />
          Thank you for your business!
          </p>
          <ReturnImage src={returnImage} alt="Return Packaging" />
          
        </div>
      );
  };

  const renderStep1 = () => (
    <div>
      {/* <h4 className="modal-step-title">Service Cancellation</h4> */}
      <p className="modal-step-description">
        Please note that your subscription for this camera will be automatically cancelled once it has been scanned at any FedEx drop off location using the proper return label and packaging.
      </p>
      <p className="modal-step-description">
        If you have lost your prepaid return label or your shipping box, new labels and boxes can be requested here.
      </p>
      <div className="modal-footer">
        <Button onClick={() => setStep(2)}>I Understand</Button>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div>
      <h4 className="modal-step-title">Original Packaging</h4>
      <p className="modal-step-description">Do you have your original box and foam inserts, and are they in adequate condition for returning your camera system?</p>
      <div className="modal-footer">
        {/* <Button onClick={() => setStep(1)}>Back</Button> */}
        <Button onClick={() => {
          setHasOriginalBox(true);
          setStep(3);
        }}>Yes</Button>
        <Button onClick={() => {
          setHasOriginalBox(false);
          setStep(3);
        }}>No</Button>
      </div>
    </div>
  );

  const renderStep3 = () => {
    if (hasOriginalBox) {
      return (
        <div>
          <h4 className="modal-step-title">Return Label</h4>
          <p className="modal-step-description">Can you locate the return label sticker that was included inside your plastic instructions packet, behind the instruction manual?</p>
          <div className="modal-footer">
            <Button onClick={() => setStep(2)}>Back</Button>
            <LoaderButton 
              isLoading={isGeneratingLabel} 
              onClick={() => {
                requestCancellation(false);
                setHasReturnLabel(true);
              }} 
              disabled={!shippingAddress || isEditingAddress}
            >
                Yes
            </LoaderButton>
            <Button onClick={() => {
              setHasReturnLabel(false);
              setStep(4);
            }}>No</Button>
          </div>
        </div>
      );
    } else {
      return (
        <div>
          <h4 className="modal-step-title">Shipping Address</h4>
          <p className="modal-step-description">This is the original shipping address we have on file. Is it still accurate and the most convenient location to receive new packaging?</p>
          {shippingAddress ? (
            <AddressDisplay
              address={shippingAddress}
              setShippingAddress={setShippingAddress}
              isEditingAddress={isEditingAddress}
              setIsEditingAddress={setIsEditingAddress}
              builderId={builderId}
            />
          ) : (
            <p className="modal-step-description">No shipping address found. Please update your billing information in your account settings.</p>
          )}
          <div className="modal-footer">
            <Button onClick={() => setStep(2)}>Back</Button>
            <LoaderButton isLoading={isGeneratingLabel} onClick={() => requestCancellation(true)} disabled={!shippingAddress || isEditingAddress}>Confirm Address</LoaderButton>
          </div>
        </div>
      );
    }
  };

  const renderStep4 = () => {
    if (hasOriginalBox && hasReturnLabel) {
      return (
        <div>
          <ReturnInstructions packaging={true} />
          <div className="modal-footer">
            {/* <Button onClick={() => setStep(3)}>Back</Button> */}
            <Button onClick={handleClose}>Close</Button>
          </div>
        </div>
      );
    } else if (hasOriginalBox && !hasReturnLabel) {
      return (
        <div>
          <h4 className="modal-step-title">Generate Return Label</h4>
          <p className="modal-step-description">Is your shipping address correct?</p>
          <AddressDisplay
            address={shippingAddress}
            setShippingAddress={setShippingAddress}
            isEditingAddress={isEditingAddress}
            setIsEditingAddress={setIsEditingAddress}
            builderId={builderId}
          />
          <div className="modal-footer">
            <Button onClick={() => setStep(3)}>Back</Button>
            <LoaderButton 
              isLoading={isGeneratingLabel}
              onClick={async () => {
                const labelUrl = await handleGenerateLabel();
                if (labelUrl) {
                  setHasReturnLabel(true);
                }
              }} 
              disabled={!shippingAddress || isEditingAddress}
            >
              Generate Label
            </LoaderButton>
          </div>
        </div>
      );
    } else {
      return (
        <div>
          <h4 className="modal-step-title">Your request for new return packaging is processing</h4>
          <p className="modal-step-description">We appreciate your business! You will receive a tracking number for the new return packaging as soon as it is shipped.
          </p>
          <ReturnInstructions packaging={false} />    
          <div className="modal-footer">
            {/* <Button onClick={() => setStep(3)}>Back</Button> */}
            <Button onClick={handleClose}>Close</Button>
          </div>
        </div>
      );
    }
  };

  return (
    <Modal 
      show={show} 
      onHide={handleClose} 
      size="lg" 
      className="camera-modal"
      enforceFocus={false}
      restoreFocus={false}
    >
      <Modal.Header closeButton>
        <Modal.Title>Cancel Service - {cap?.name}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {step === 1 && renderStep1()}
        {step === 2 && renderStep2()}
        {step === 3 && renderStep3()}
        {step === 4 && renderStep4()}
      </Modal.Body>
    </Modal>
  );
} 